import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Button,
  Modal,
  Space,
  Typography,
  Card,
  Checkbox,
  Image,
  Divider, // Added Divider
} from 'antd';
import { EditOutlined } from '@ant-design/icons'; // For the edit icon next to "规格"
import { getGoodsInfo } from '@/services/xiaohongshu';
import FullScreenSpin from '@/components/FullscreenSpin';

interface GoodsSelectorProps {
  name: string; // Assuming 'name' is used for fetching or context, keep if needed
  visible: boolean;
  onCancel: () => void; // Simplified onCancel
  onFinish: (selectedGoods: Goods[]) => void;
}

// Goods interface remains the same
interface Goods {
  goods_id: string;
  item_id: string; // Used as key
  name: string;
  purchase_price: number;
  purchase_price_range: string; // This might be what's displayed as price
  image: string;
}

const { Text } = Typography;
const MAX_SELECTION_COUNT = 18;

const GoodsSelector: React.FC<GoodsSelectorProps> = ({
  name, // Keep if used by getGoodsInfo or other logic
  visible,
  onCancel,
  onFinish,
}) => {
  const [processing, setProcessing] = useState(false);
  const [allGoods, setAllGoods] = useState<Goods[]>([]);
  const [selectedGoodsMap, setSelectedGoodsMap] = useState<
    Record<string, Goods>
  >({}); // Using item_id as key for selection

  useEffect(() => {
    const fetchGoods = async () => {
      if (visible) {
        setProcessing(true);
        try {
          // Assuming getGoodsInfo fetches all available goods
          // Adjust the API call and response mapping as needed
          const response = (await getGoodsInfo({ name })) as any;
          if (response.resp.response && Array.isArray(response.resp.response)) {
            // Assuming the actual goods items are in response.resp.response
            // and each item directly matches the Goods interface or needs minimal mapping
            setAllGoods(
              response.resp.response.map((r: any) => r.goods_info || r),
            );
          } else {
            setAllGoods([]);
          }
        } catch (error) {
          console.error('Failed to fetch goods:', error);
          setAllGoods([]);
        } finally {
          setProcessing(false);
        }
      }
    };

    fetchGoods();
  }, [visible]);

  const handleToggleSelection = (good: Goods) => {
    setSelectedGoodsMap((prevMap) => {
      const newMap = { ...prevMap };
      if (newMap[good.item_id]) {
        delete newMap[good.item_id];
      } else {
        if (Object.keys(newMap).length < MAX_SELECTION_COUNT) {
          newMap[good.item_id] = good;
        } else {
          // Optionally, show a message that max selection is reached
          console.warn(`Max selection of ${MAX_SELECTION_COUNT} reached.`);
        }
      }
      return newMap;
    });
  };

  const handleSave = () => {
    onFinish(Object.values(selectedGoodsMap));
    onCancel(); // Close modal after saving
  };

  const selectedCount = Object.keys(selectedGoodsMap).length;

  return (
    <>
      {processing && <FullScreenSpin />}
      <Modal
        title="选择商品" // Or null if no title bar is desired, matching image more closely
        open={visible}
        onCancel={onCancel}
        width={800} // Or adjust as needed
        bodyStyle={{ padding: 0 }} // To make the list flush with modal edges if desired
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 24px',
            }}
          >
            <Text>
              已选择 {selectedCount} 项，最多不超过 {MAX_SELECTION_COUNT} 项
            </Text>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={selectedCount === 0} // Disable save if nothing is selected
              style={{
                backgroundColor: '#ff4d4f',
                borderColor: '#ff4d4f',
                color: '#fff',
              }}
            >
              保存
            </Button>
          </div>
        }
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '24px' }}>
          {' '}
          {/* Scrollable content area */}
          {allGoods.length === 0 && !processing && (
            <Text
              style={{ display: 'block', textAlign: 'center', padding: '20px' }}
            >
              暂无商品可选。
            </Text>
          )}
          <Space direction="vertical" style={{ width: '100%' }} size={0}>
            {allGoods.map((item, index) => (
              <React.Fragment key={item.item_id}>
                <Card bordered={false} bodyStyle={{ padding: '16px 0' }}>
                  <Row gutter={[16, 16]} align="middle" wrap={false}>
                    <Col>
                      <Checkbox
                        checked={!!selectedGoodsMap[item.item_id]}
                        onChange={() => handleToggleSelection(item)}
                        disabled={
                          !selectedGoodsMap[item.item_id] &&
                          selectedCount >= MAX_SELECTION_COUNT
                        }
                      />
                    </Col>
                    <Col>
                      <Image
                        width={60} // Adjust size as needed
                        height={60}
                        src={item.image || 'https://via.placeholder.com/60'} // Placeholder if no image
                        preview={false}
                        style={{ objectFit: 'cover' }}
                      />
                    </Col>
                    <Col flex="auto">
                      <Space direction="vertical" size={2}>
                        <Text ellipsis={{ tooltip: item.name }}>
                          {item.name}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          商品ID: {item.item_id}
                        </Text>
                      </Space>
                    </Col>
                    <Col>
                      <Text
                        style={{
                          color: '#ff4d4f',
                          fontWeight: 'bold',
                          fontSize: '16px',
                        }}
                      >
                        {item.purchase_price_range ||
                          '¥' + (item.purchase_price / 100)?.toFixed(2) ||
                          '¥0.00'}
                      </Text>
                    </Col>
                  </Row>
                </Card>
                {index < allGoods.length - 1 && (
                  <Divider style={{ margin: '0' }} />
                )}
              </React.Fragment>
            ))}
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default GoodsSelector;
